/**
 * Leitura da mensagem de erro que a API devolve.
 *
 * Existe porque o toast genérico ("Erro ao salvar. Tente novamente.") vinha sendo exibido mesmo
 * quando o backend já tinha mandado o motivo real e acionável. O corpo de erro chega em formatos
 * bem diferentes e cada tela tratava um subconjunto deles:
 *
 *  1. `{ messages: string[] }`                    — envelope do GMCore
 *  2. `{ errors: string[] }`                      — variante do mesmo envelope
 *  3. `{ errors: { Campo: string[] } }`           — ValidationProblemDetails do ASP.NET, em que
 *                                                   `errors` é OBJETO. `Array.isArray` dava falso
 *                                                   e a mensagem era descartada em silêncio.
 *  4. `{ message: string }` / `{ error: string }` — envelope simples
 *  5. `{ detail, title }`                         — ProblemDetails
 *  6. `"texto"`                                   — corpo string puro
 *  7. `Blob`                                      — resposta de download (`responseType: 'blob'`).
 *                                                   Exige leitura assíncrona: ver `resolveApiErrorMessage`.
 *
 * O que este módulo deliberadamente **não** usa é o `message` do `HttpErrorResponse`: ele sempre
 * existe e vale algo como `Http failure response for https://...: 400 OK`. Usá-lo como fallback
 * troca um genérico em português por uma URL na cara do usuário.
 */

/**
 * Título padrão do ASP.NET quando a validação falha. É boilerplate em inglês e não informa nada
 * além do que o genérico da tela já diz — quando é só isso que sobra, é melhor deixar o chamador
 * usar o fallback dele, em português.
 */
const ASPNET_VALIDATION_TITLE = 'one or more validation errors occurred.';

/** Um corpo de texto longo ou com marcação é página de erro do servidor, não mensagem de negócio. */
const MAX_PLAIN_TEXT_LENGTH = 300;

function firstNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function joinStrings(values: unknown[]): string | null {
  const parts = values.map((v) => String(v ?? '').trim()).filter(Boolean);
  return parts.length ? parts.join(' ') : null;
}

/** `{ errors: { Campo: ["msg"] } }` — achata os valores, ignorando entrada que não seja lista. */
function fromValidationErrors(errors: Record<string, unknown>): string | null {
  const messages: unknown[] = [];
  for (const value of Object.values(errors)) {
    if (Array.isArray(value)) messages.push(...value);
    else if (typeof value === 'string') messages.push(value);
  }
  return joinStrings(messages);
}

/** Aceita tanto o `HttpErrorResponse` quanto o corpo já desembrulhado. */
function unwrapBody(error: unknown): unknown {
  if (error && typeof error === 'object' && 'error' in (error as any)) {
    return (error as any).error;
  }
  return error;
}

/**
 * Mensagem de negócio do erro, ou `null` quando a API não mandou nada aproveitável — e aí o
 * chamador usa o texto dele. Síncrono: corpo em `Blob` devolve `null`, use `resolveApiErrorMessage`.
 */
export function extractApiErrorMessage(error: unknown): string | null {
  const body = unwrapBody(error);

  if (typeof body === 'string') {
    const text = body.trim();
    if (!text || text.length > MAX_PLAIN_TEXT_LENGTH || text.includes('<')) return null;
    return text;
  }

  if (!body || typeof body !== 'object' || body instanceof Blob) return null;

  const record = body as Record<string, unknown>;

  if (Array.isArray(record['messages'])) {
    const joined = joinStrings(record['messages']);
    if (joined) return joined;
  }

  const errors = record['errors'];
  if (Array.isArray(errors)) {
    const joined = joinStrings(errors);
    if (joined) return joined;
  } else if (errors && typeof errors === 'object') {
    const joined = fromValidationErrors(errors as Record<string, unknown>);
    if (joined) return joined;
  }

  const direct =
    firstNonEmptyString(record['message']) ??
    firstNonEmptyString(record['error']) ??
    firstNonEmptyString(record['detail']);
  if (direct) return direct;

  const title = firstNonEmptyString(record['title']);
  if (title && title.toLowerCase() !== ASPNET_VALIDATION_TITLE) return title;

  return null;
}

/**
 * Igual ao `extractApiErrorMessage`, mas também lê corpo em `Blob` — o caso de toda requisição com
 * `responseType: 'blob'` (exportar XLSX/PDF). Nesses endpoints o Angular entrega o erro como Blob,
 * então qualquer checagem de string ou array falha e a mensagem se perde por construção.
 */
export async function resolveApiErrorMessage(error: unknown): Promise<string | null> {
  const body = unwrapBody(error);
  if (!(body instanceof Blob)) return extractApiErrorMessage(error);

  let text = '';
  try {
    text = (await body.text()).trim();
  } catch {
    return null;
  }
  if (!text) return null;

  try {
    // O corpo do Blob é o mesmo envelope de sempre, só que serializado.
    return extractApiErrorMessage({ error: JSON.parse(text) });
  } catch {
    // Não era JSON: só aproveita se parecer mensagem, e não página de erro do servidor.
    return text.length <= MAX_PLAIN_TEXT_LENGTH && !text.includes('<') ? text : null;
  }
}
